import Card from "@/components/Common/Card";
import PaperIcon from "@/assets/images/paper.png";
import { useMemo } from "react";
import ScenarioHeaderActions from "./ScenarioHeaderActions";
import { cn } from "@/libs/utils";
import { Scenario } from "@/types/scenario";
import { calculateScenarioBidSummary } from "../utils";
import ScenarioGroupTable from "./table/ScenarioGroupTable";
import { Divide } from "lucide-react";
import ScenarioApprovalDialog from "./ScenarioApprovalDialog";
import { useBudgetAmountMap } from "../hooks/useBudgetAmountMap";
import ScenarioHeaderStatus from "./ScenarioHeaderStatus";
import { useReadScenarioApprovalsById } from "@/libs/react-query/hooks/useScenarioApprovalsQuery";
import { ScenarioDecision } from "@/consts/Scenario";

type Props = {
  scenarios: Scenario[];
};

// approval_id가 있어도 final_decision을 체크하는 컴포넌트
function ScenarioHeaderActionsWithApprovalCheck({
  scenario,
  scenarios,
}: {
  scenario: Scenario;
  scenarios: Scenario[];
}) {
  const { data: approvalData } = useReadScenarioApprovalsById(
    scenario.approval_id || ""
  );

  // 승인이 취소된 경우 햄버거 메뉴 표시
  if (approvalData?.final_decision === ScenarioDecision.CANCELED) {
    return <ScenarioHeaderActions scenario={scenario} scenarios={scenarios} />;
  }

  // 그 외의 경우 승인 상태 표시
  return (
    <ScenarioHeaderStatus
      approval_id={scenario.approval_id!}
      scenario={scenario}
      scenarios={scenarios}
    />
  );
}

// 승인 상태에 따라 ScenarioApprovalDialog 표시 여부를 결정하는 컴포넌트
function ScenarioTrailingActionsWithApprovalCheck({
  approvalId,
}: {
  approvalId: string;
}) {
  const { data: approvalData } = useReadScenarioApprovalsById(approvalId);

  // 승인이 취소된 경우 다이얼로그 표시하지 않음
  if (approvalData?.final_decision === ScenarioDecision.CANCELED) {
    return null;
  }

  return <ScenarioApprovalDialog approvalId={approvalId} />;
}

export default function ScenarioList({ scenarios }: Props) {
  const { hasConfirmedScenario, confirmedScenarioId } = useMemo(() => {
    const scenario = scenarios.find((scenario) => !!scenario.approval_id);
    return {
      hasConfirmedScenario: !!scenario,
      confirmedScenarioId: scenario?.scenario_id || null,
    };
  }, [scenarios]);

  const projectIds = useMemo(
    () => scenarios.map((s) => s.project_id).filter(Boolean),
    [scenarios]
  );

  const { budgetMap, isLoading } = useBudgetAmountMap(projectIds);

  const renderHeaderActions = (scenario: Scenario) => {
    if (scenario.approval_id) {
      // approval_id가 있는 경우 승인 상태를 체크해서 결정
      return (
        <ScenarioHeaderActionsWithApprovalCheck
          scenario={scenario}
          scenarios={scenarios}
        />
      );
    }

    // approval_id가 없는 경우 햄버거 메뉴 표시
    return <ScenarioHeaderActions scenario={scenario} scenarios={scenarios} />;
  };

  const renderTrailingActions = (scenario: Scenario) => {
    if (scenario.approval_id) {
      return (
        <ScenarioTrailingActionsWithApprovalCheck
          approvalId={scenario.approval_id}
        />
      );
    }

    return null;
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      {scenarios.map((scenario) => {
        const { groupList, bidAmount, bidRate, baseAmountMap } =
          calculateScenarioBidSummary({
            scenarioData: scenario.data,
            budgetAmountMap: budgetMap[scenario.project_id] || {
              budget: 0,
              basisAmount: undefined,
            },
          });

        return (
          <div key={scenario.scenario_id} className="relative">
            <Card
              title={scenario.title}
              icon={PaperIcon}
              className="flex"
              headerActions={renderHeaderActions(scenario)}
              trailingActions={renderTrailingActions(scenario)}
            >
              <div className="flex flex-col justify-between gap-4 p-5 flex-1">
                <ScenarioGroupTable
                  data={groupList}
                  onDiscountRateChange={() => {}}
                  setData={() => {}}
                  readonly={true}
                />

                <div className="flex items-center gap-1 text-sm font-semibold">
                  <span className="text-gray-B">투찰률</span>
                  <span className="text-dark-red">
                    <span className="text-dark-red">
                      {bidRate.toFixed(2)}% =
                    </span>
                  </span>
                  <span className="text-gray-C"> 투찰 금액</span>
                  <span className="text-gray-A">
                    <span className="text-gray-A">
                      {bidAmount.toLocaleString()}
                    </span>
                  </span>
                  <Divide className="text-gray-B" size={12} />
                  <span className="text-gray-C">
                    {baseAmountMap.basisAmount ? "기초 금액" : "배정 예산"}
                  </span>
                  <span className="text-gray-A">
                    <span className="text-gray-A">
                      {(
                        baseAmountMap.basisAmount || baseAmountMap.budget
                      ).toLocaleString()}
                    </span>
                  </span>
                </div>
              </div>
            </Card>
          </div>
        );
      })}
    </div>
  );
}
